import { Icon } from '@iconify/react'
import { Box, Collapse, IconButton, Typography, useTheme } from '@mui/material'
import { memo, useMemo, useRef, useState } from 'react'
import KanbanItem from './KanbanItem'
import type { Kanban, Material } from './types'

interface PalletItemProps extends Material {
  onQuantityAdjust?: (kanban: Kanban) => void
  onScanKanban?: (kanban: Kanban) => void
  onScanPallet?: (pallet: { name: string; code: string; qrCodeImage?: string }) => void
  actionPosition?: 'left' | 'right'
  viewOnly?: boolean
}

const PalletItem = memo<PalletItemProps>(
  ({
    name,
    code,
    scannedCount,
    kanbanCount,
    orderQty,
    kanbans,
    palletScanned,
    palletQrCodeImage,
    onQuantityAdjust,
    onScanKanban,
    onScanPallet,
    actionPosition = 'right',
    viewOnly = false
  }) => {
    const theme = useTheme()
    const [expanded, setExpanded] = useState(false)

    const REVEAL_WIDTH = 54
    const SIDEBAR_MIN = 18
    const THRESHOLD = 24
    const [translateX, setTranslateX] = useState(0)
    const [dragging, setDragging] = useState(false)
    const startXRef = useRef(0)

    const onTouchStart = (e: React.TouchEvent) => {
      if (viewOnly) return
      startXRef.current = e.touches[0].clientX
      setDragging(true)
    }
    const onTouchMove = (e: React.TouchEvent) => {
      if (viewOnly || !dragging) return
      const currentX = e.touches[0].clientX
      const delta = startXRef.current - currentX
      if (delta <= 0) {
        setTranslateX(0)
      } else {
        setTranslateX(Math.min(REVEAL_WIDTH, delta))
      }
    }
    const onTouchEnd = () => {
      if (viewOnly) return
      setDragging(false)
      if (translateX >= THRESHOLD) {
        onScanPallet?.({ name, code, qrCodeImage: palletQrCodeImage })
        setTranslateX(0)
      } else {
        setTranslateX(0)
      }
    }

    const status = useMemo(() => {
      const hasMismatch = kanbans.some(k => k.hasQuantityMismatch)
      const hasScanned = scannedCount > 0 || Boolean(palletScanned)

      if (hasMismatch) return 'warning'
      if (hasScanned) return 'success'
      return 'danger'
    }, [kanbans, scannedCount, palletScanned])

    const statusColors = theme.colorToken.background[status]
    const getBackgroundColor = () => {
      switch (status) {
        case 'warning':
          return theme.colorToken.background.warning.normal
        case 'success':
          return theme.colorToken.background.success.normal
        default:
          return theme.colorToken.background.danger.normal
      }
    }
    const borderColor = expanded ? statusColors.normal : theme.palette.neutral[50]

    const panelWidth = Math.max(SIDEBAR_MIN, Math.min(REVEAL_WIDTH, translateX))

    const ActionPanel = (
      <Box
        onClick={() => onScanPallet?.({ name, code, qrCodeImage: palletQrCodeImage })}
        sx={{
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          width: panelWidth,
          background: getBackgroundColor(),
          color: theme.colorToken.text.neutralInverted.normal,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          borderTopRightRadius: 8,
          borderBottomRightRadius: 0,
          userSelect: 'none',
          zIndex: 3,
          pointerEvents: onScanPallet ? 'auto' : 'none'
        }}
      >
        {panelWidth <= SIDEBAR_MIN + 6 ? (
          <Icon icon={'tabler:chevrons-left'} color='white' fontSize={12} />
        ) : (
          <>
            <Icon icon={'tabler:scan'} color='white' fontSize={14} />
            <Typography fontSize={10} color='white' variant='caption'>
              Pallet
            </Typography>
          </>
        )}
      </Box>
    )

    return (
      <Box
        sx={{
          position: 'relative',
          background: 'white',
          borderRadius: 1.5,
          border: `1px solid ${borderColor}`,
          mb: 1.5,
          overflow: 'hidden'
        }}
      >
        <Box
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          onClick={() => {
            if (!dragging) setExpanded(!expanded)
          }}
          sx={{
            p: 1.5,
            pr: 3,
            py: 4,
            background: viewOnly ? 'white' : expanded ? statusColors.subtlest : 'white',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            cursor: 'pointer',
            position: 'relative',
            zIndex: 1,
            touchAction: 'pan-y',
            borderBottom: expanded && !viewOnly ? `1px solid ${getBackgroundColor()}` : 'none'
          }}
        >
          {!viewOnly && ActionPanel}
          <IconButton size='medium'>
            <Icon
              icon={expanded ? 'tabler:chevron-down' : 'tabler:chevron-right'}
              color={theme.colorToken.icon.neutral.boldest}
            />
          </IconButton>

          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant='body2' fontWeight={500}>
              {name}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant='caption' color='text.secondary'>
                {code}
              </Typography>
              {!viewOnly && (
                <Box
                  sx={{
                    px: 0.5,
                    py: 0.25,
                    background: statusColors.subtlest,
                    color: theme.colorToken.text[status].normal,
                    borderRadius: 0.5,
                    fontSize: 12
                  }}
                >
                  {scannedCount > 0 ? `${scannedCount} Scanned` : palletScanned ? 'Scanned' : 'Unscanned'}
                </Box>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
              <Icon icon='tabler:squares-filled' fontSize={14} />
              <Typography variant='caption'>{kanbanCount} Kanban</Typography>
            </Box>
          </Box>

          <Box
            sx={{
              width: 36,
              height: 36,
              background: theme.colorToken.background.primary.subtlest,
              color: theme.colorToken.text.primary.normal,
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 12,
              fontWeight: 500,
              transform: viewOnly ? 'none' : `translateX(-${translateX - 17}px)`,
              transition: viewOnly || dragging ? 'none' : 'transform 220ms ease-out',
              marginRight: 4
            }}
          >
            {orderQty}
          </Box>
        </Box>

        <Collapse in={expanded}>
          <Box sx={{ p: 3, pb: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {kanbans.map((kanban, index) => (
              <KanbanItem
                key={`${kanban.name}-${index}`}
                {...kanban}
                actionPosition={actionPosition}
                onQuantityAdjust={onQuantityAdjust}
                onScanKanban={onScanKanban}
                disableSwipe
                viewOnly={viewOnly}
              />
            ))}
          </Box>
        </Collapse>
      </Box>
    )
  }
)

export default PalletItem
